import { Suspense, lazy } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MaskLines } from "@/components/Reveal";
import { Magnetic } from "@/components/Magnetic";
import { scrollToHash } from "@/lib/api";

const HardwareShowcase = lazy(() =>
  import("@/components/HardwareShowcase").then((m) => ({ default: m.HardwareShowcase }))
);

export const Hero = () => (
  <section className="relative min-h-screen flex items-center overflow-hidden bg-white" data-testid="hero-section">
    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[46rem] h-[46rem] rounded-full bg-[#F3F4F6] blur-3xl opacity-70 pointer-events-none" />

    <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 w-full pt-32 pb-24 grid lg:grid-cols-2 gap-10 items-center">
      <div className="relative z-10">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-8"
          data-testid="hero-overline"
        >
          Royal Deals FZ-LLC — Technology Supply
        </motion.p>

        <h1 className="font-heading font-light tracking-tight leading-[1.02] text-midnight text-5xl sm:text-6xl lg:text-[4.1rem]" data-testid="hero-headline">
          <MaskLines lines={["Powering the Next", "Generation"]} delay={0.35} />
          <span className="block overflow-hidden pb-[0.12em] -mb-[0.12em]">
            <motion.span
              className="block whitespace-nowrap"
              initial={{ y: "115%" }}
              animate={{ y: "0%" }}
              transition={{ duration: 1, delay: 0.61, ease: [0.22, 1, 0.36, 1] }}
            >
              of <span className="gold-text-gradient font-medium">Technology</span>
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.95 }}
          className="text-midnight font-medium text-lg mt-8 max-w-xl leading-relaxed"
          data-testid="hero-subline"
        >
          Advanced compute, enterprise hardware, computer components and global technology supply.
        </motion.p>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="text-midnight/60 text-base mt-4 max-w-xl leading-relaxed"
        >
          Royal Deals FZ-LLC connects businesses, resellers, integrators and institutional buyers with technology products through international sourcing and professional commercial execution.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="flex flex-wrap items-center gap-4 mt-10"
        >
          <Magnetic>
            <Link
              to="/request"
              data-testid="hero-request-hardware"
              className="inline-block bg-midnight text-white text-sm font-medium px-8 py-4 hover:bg-midnight-light transition-colors"
            >
              Request Hardware
            </Link>
          </Magnetic>
          <Magnetic>
            <a
              href="#builder"
              onClick={(e) => { e.preventDefault(); scrollToHash("#builder"); }}
              data-testid="hero-submit-requirement"
              className="inline-block border border-gold text-gold text-sm font-medium px-8 py-4 hover:bg-gold/5 transition-colors"
            >
              Submit Requirement
            </a>
          </Magnetic>
          <a
            href="#capabilities"
            onClick={(e) => { e.preventDefault(); scrollToHash("#capabilities"); }}
            data-testid="hero-explore-capabilities"
            className="text-sm font-medium text-midnight/50 hover:text-gold transition-colors"
          >
            Explore what we supply →
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="flex flex-wrap gap-x-8 gap-y-2 mt-14 pt-8 border-t border-midnight/10 text-xs font-medium tracking-wide text-midnight/45"
        >
          <span data-testid="hero-tag-uae">UAE Based — Registered in Ras Al Khaimah</span>
          <span>GCC · Europe · Africa · Asia-Pacific · Americas</span>
          <span>B2B Procurement Only</span>
        </motion.div>
      </div>

      <div className="relative h-[420px] lg:h-[540px]" data-testid="hero-visual">
        <Suspense fallback={null}>
          <HardwareShowcase />
        </Suspense>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.6 }}
          className="absolute bottom-1 left-0 z-40 max-w-[52%] flex flex-wrap gap-x-6 gap-y-1 text-[10px] font-bold uppercase tracking-[0.15em] text-midnight/40"
        >
          <a href="#capabilities" onClick={(e) => { e.preventDefault(); scrollToHash("#capabilities"); }} data-testid="hero-cat-gpu" className="hover:text-gold transition-colors">Enterprise GPU Compute</a>
          <a href="#capabilities" onClick={(e) => { e.preventDefault(); scrollToHash("#capabilities"); }} data-testid="hero-cat-memory" className="hover:text-gold transition-colors">High-Performance Memory</a>
          <a href="#capabilities" onClick={(e) => { e.preventDefault(); scrollToHash("#capabilities"); }} data-testid="hero-cat-storage" className="hover:text-gold transition-colors">Enterprise Storage</a>
        </motion.div>
      </div>
    </div>
  </section>
);
